import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  User, 
  Bell, 
  Link2, 
  LogOut,
  Check,
  Loader2,
  Smartphone,
  BellRing,
  MessageCircle,
  Trophy,
  Megaphone,
  Clock,
  Calendar,
  Save,
  FileText,
  Download,
  Sun,
  Moon,
  Palette
} from 'lucide-react';

const integrationApps = [
  { id: 'apple_health', name: 'Apple Health', icon: '🍎', description: 'Sync health data from your iPhone' },
  { id: 'google_fit', name: 'Google Fit', icon: '🏃', description: 'Connect your Google fitness data' },
  { id: 'garmin', name: 'Garmin', icon: '⌚', description: 'Sync your Garmin device' },
  { id: 'fitbit', name: 'Fitbit', icon: '💪', description: 'Import Fitbit activity data' },
  { id: 'strava', name: 'Strava', icon: '🚴', description: 'Track runs and rides' }
];

const DAYS_OF_WEEK = [
  { id: 'monday', label: 'Mon' },
  { id: 'tuesday', label: 'Tue' },
  { id: 'wednesday', label: 'Wed' },
  { id: 'thursday', label: 'Thu' },
  { id: 'friday', label: 'Fri' },
  { id: 'saturday', label: 'Sat' },
  { id: 'sunday', label: 'Sun' }
];

export default function SettingsPage() {
  const { user, token, logout } = useAuth();
  const [integrations, setIntegrations] = useState({ connected: [], available: [] });
  const [connectingApp, setConnectingApp] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({
    workout_reminders: true,
    trainer_messages: true,
    achievements: true,
    broadcasts: true,
    reminder_time: '08:00',
    reminder_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
  });
  const [browserNotificationStatus, setBrowserNotificationStatus] = useState('default');
  
  // Theme state
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('bba_theme') || 'dark';
  });
  
  // PDF Export state
  const [exportOptions, setExportOptions] = useState({
    include_workouts: true,
    include_nutrition: true,
    include_checkins: true,
    include_achievements: true,
    days: 30
  });
  const [exporting, setExporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    fetchIntegrations();
    fetchNotificationPreferences();
    fetchExportPreview();
    if ('Notification' in window) {
      setBrowserNotificationStatus(Notification.permission);
    }
    // Apply theme on mount
    document.documentElement.setAttribute('data-theme', theme);
  }, [token]);
  
  // Theme toggle handler
  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('bba_theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    toast.success(`Switched to ${newTheme} mode`);
  };
  
  // Fetch export preview
  const fetchExportPreview = async () => {
    try {
      const response = await axios.get(`${API}/export/preview?days=${exportOptions.days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewData(response.data);
    } catch (error) {
      console.error('Error fetching export preview:', error);
    }
  };
  
  // Generate PDF
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        include_workouts: exportOptions.include_workouts,
        include_nutrition: exportOptions.include_nutrition,
        include_checkins: exportOptions.include_checkins,
        include_achievements: exportOptions.include_achievements,
        days: exportOptions.days
      });
      
      const response = await axios.post(
        `${API}/export/pdf?${params.toString()}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BBA_Progress_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF report downloaded successfully!');
    } catch (error) {
      toast.error('Failed to generate PDF report');
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrations(response.data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  const fetchNotificationPreferences = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setNotificationPrefs({
          workout_reminders: response.data.workout_reminders ?? true,
          trainer_messages: response.data.trainer_messages ?? true,
          achievements: response.data.achievements ?? true,
          broadcasts: response.data.broadcasts ?? true,
          reminder_time: response.data.reminder_time || '08:00',
          reminder_days: response.data.reminder_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
        });
      }
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
    }
  }, [token]);

  const saveNotificationPreferences = async () => {
    setSavingPrefs(true);
    try {
      await axios.put(
        `${API}/notifications/preferences`,
        null,
        {
          params: {
            workout_reminders: notificationPrefs.workout_reminders,
            trainer_messages: notificationPrefs.trainer_messages,
            achievements: notificationPrefs.achievements,
            broadcasts: notificationPrefs.broadcasts,
            reminder_time: notificationPrefs.reminder_time,
            reminder_days: notificationPrefs.reminder_days.join(',')
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const connectApp = async (appId) => {
    setConnectingApp(appId);
    try {
      await axios.post(
        `${API}/integrations/connect?app_name=${appId}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${integrationApps.find(a => a.id === appId)?.name} connected!`);
      fetchIntegrations();
    } catch (error) {
      toast.error('Failed to connect app');
    } finally {
      setConnectingApp(null);
    }
  };

  const requestBrowserNotifications = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setBrowserNotificationStatus(permission);
      if (permission === 'granted') {
        new Notification('Notifications Enabled!', {
          body: 'You will now receive workout reminders and messages.',
          icon: '/logo192.png'
        });
        toast.success('Browser notifications enabled');
      } else if (permission === 'denied') {
        toast.error('Notifications blocked. Please enable in browser settings.');
      }
    }
  };

  const sendTestNotification = async () => {
    try {
      await axios.post(`${API}/notifications/test`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test notification sent! Check your notification bell.');
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  const toggleReminderDay = (dayId) => {
    setNotificationPrefs(prev => ({
      ...prev,
      reminder_days: prev.reminder_days.includes(dayId)
        ? prev.reminder_days.filter(d => d !== dayId)
        : [...prev.reminder_days, dayId]
    }));
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  return (
    <div data-testid="settings-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
          <span className="text-primary">SETTINGS</span>
        </h1>
        <p className="text-gray-400 mt-1">Manage your account and preferences</p>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="bg-secondary/50 border border-border flex-wrap">
          <TabsTrigger value="profile" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <User className="w-4 h-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="integrations" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Link2 className="w-4 h-4 mr-2" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="hsa-fsa" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />
            HSA/FSA
          </TabsTrigger>
          <TabsTrigger value="appearance" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Palette className="w-4 h-4 mr-2" />
            Appearance
          </TabsTrigger>
          <TabsTrigger value="export" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />
            Export
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-6">
          <div className="max-w-2xl">
            <div className="card-tactical">
              <div className="flex items-center gap-6 mb-8">
                <Avatar className="w-20 h-20">
                  <AvatarFallback className="bg-primary/20 text-primary text-2xl">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="text-xl text-white font-semibold">
                    {user?.first_name} {user?.last_name}
                  </h2>
                  <p className="text-gray-400">{user?.email}</p>
                  <p className="text-sm text-primary capitalize mt-1">{user?.role}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="space-y-2">
                  <Label className="text-gray-300">First Name</Label>
                  <Input
                    value={user?.first_name || ''}
                    className="bg-secondary border-transparent"
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Last Name</Label>
                  <Input
                    value={user?.last_name || ''}
                    className="bg-secondary border-transparent"
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Email</Label>
                  <Input
                    value={user?.email || ''}
                    className="bg-secondary border-transparent"
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Fitness Level</Label>
                  <Input
                    value={user?.fitness_level || 'Not set'}
                    className="bg-secondary border-transparent capitalize"
                    disabled
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 p-4 bg-secondary/50 rounded-sm mb-6">
                <div className="text-center">
                  <div className="font-mono text-2xl text-white">{user?.age || '-'}</div>
                  <div className="text-xs text-gray-400">Age</div>
                </div>
                <div className="text-center">
                  <div className="font-mono text-2xl text-white">{user?.height_cm || '-'}cm</div>
                  <div className="text-xs text-gray-400">Height</div>
                </div>
                <div className="text-center">
                  <div className="font-mono text-2xl text-white">{user?.weight_kg || '-'}kg</div>
                  <div className="text-xs text-gray-400">Weight</div>
                </div>
              </div>

              <Button 
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="destructive"
                className="w-full"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="mt-6">
          <div className="max-w-2xl">
            <div className="card-tactical mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Smartphone className="w-6 h-6 text-primary" />
                <div>
                  <h3 className="text-lg text-white font-semibold">Connected Apps</h3>
                  <p className="text-sm text-gray-400">Sync your fitness data from other apps</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {integrationApps.map((app) => {
                  const isConnected = integrations.connected?.includes(app.id);
                  
                  return (
                    <div 
                      key={app.id}
                      className={`flex items-center justify-between p-4 rounded-sm border ${
                        isConnected ? 'border-primary/50 bg-primary/5' : 'border-border bg-secondary/30'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-2xl">{app.icon}</span>
                        <div>
                          <div className="text-white font-medium">{app.name}</div>
                          <div className="text-sm text-gray-400">{app.description}</div>
                        </div>
                      </div>
                      
                      <Button
                        data-testid={`connect-${app.id}-btn`}
                        onClick={() => !isConnected && connectApp(app.id)}
                        disabled={connectingApp === app.id || isConnected}
                        variant={isConnected ? 'ghost' : 'outline'}
                        className={isConnected ? 'text-green-500 cursor-default' : 'border-primary text-primary hover:bg-primary hover:text-white'}
                      >
                        {connectingApp === app.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : isConnected ? (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Connected
                          </>
                        ) : (
                          'Connect'
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-6">
          <div className="max-w-2xl space-y-6">
            {/* Browser Notifications Card */}
            <div className="card-tactical">
              <div className="flex items-center gap-3 mb-4">
                <BellRing className="w-6 h-6 text-primary" />
                <div>
                  <h3 className="text-lg text-white font-semibold">Browser Notifications</h3>
                  <p className="text-sm text-gray-400">Receive notifications when you're on this site</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-sm">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${
                    browserNotificationStatus === 'granted' ? 'bg-green-500' :
                    browserNotificationStatus === 'denied' ? 'bg-red-500' :
                    'bg-yellow-500'
                  }`} />
                  <span className="text-white">
                    {browserNotificationStatus === 'granted' ? 'Notifications enabled' :
                     browserNotificationStatus === 'denied' ? 'Notifications blocked' :
                     'Notifications not enabled'}
                  </span>
                </div>
                {browserNotificationStatus !== 'granted' && (
                  <Button
                    onClick={requestBrowserNotifications}
                    disabled={browserNotificationStatus === 'denied'}
                    className="bg-primary hover:bg-red-700"
                  >
                    Enable
                  </Button>
                )}
              </div>
              
              <Button
                data-testid="test-notification-btn"
                onClick={sendTestNotification}
                variant="outline"
                className="w-full mt-4 border-border"
              >
                <Bell className="w-4 h-4 mr-2" />
                Send Test Notification
              </Button>
            </div>

            {/* Notification Types Card */}
            <div className="card-tactical">
              <h3 className="text-lg text-white font-semibold mb-6">Notification Types</h3>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                      <BellRing className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="text-white font-medium">Workout Reminders</div>
                      <div className="text-sm text-gray-400">Get reminded about your scheduled workouts</div>
                    </div>
                  </div>
                  <Switch
                    data-testid="workout-reminders-switch"
                    checked={notificationPrefs.workout_reminders}
                    onCheckedChange={(checked) => setNotificationPrefs(prev => ({...prev, workout_reminders: checked}))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <div className="text-white font-medium">Trainer Messages</div>
                      <div className="text-sm text-gray-400">Notifications when trainers message you</div>
                    </div>
                  </div>
                  <Switch
                    data-testid="trainer-messages-switch"
                    checked={notificationPrefs.trainer_messages}
                    onCheckedChange={(checked) => setNotificationPrefs(prev => ({...prev, trainer_messages: checked}))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                      <Trophy className="w-5 h-5 text-yellow-500" />
                    </div>
                    <div>
                      <div className="text-white font-medium">Achievements</div>
                      <div className="text-sm text-gray-400">Badge unlocks and milestone celebrations</div>
                    </div>
                  </div>
                  <Switch
                    data-testid="achievements-switch"
                    checked={notificationPrefs.achievements}
                    onCheckedChange={(checked) => setNotificationPrefs(prev => ({...prev, achievements: checked}))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                      <Megaphone className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <div className="text-white font-medium">Broadcasts</div>
                      <div className="text-sm text-gray-400">Announcements from trainers</div>
                    </div>
                  </div>
                  <Switch
                    data-testid="broadcasts-switch"
                    checked={notificationPrefs.broadcasts}
                    onCheckedChange={(checked) => setNotificationPrefs(prev => ({...prev, broadcasts: checked}))}
                  />
                </div>
              </div>
            </div>

            {/* Reminder Schedule Card */}
            <div className="card-tactical">
              <h3 className="text-lg text-white font-semibold mb-6">Reminder Schedule</h3>
              
              <div className="space-y-6">
                <div>
                  <Label className="text-gray-300 mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Daily Reminder Time
                  </Label>
                  <Select
                    value={notificationPrefs.reminder_time}
                    onValueChange={(value) => setNotificationPrefs(prev => ({...prev, reminder_time: value}))}
                  >
                    <SelectTrigger className="bg-secondary border-transparent w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="06:00">6:00 AM</SelectItem>
                      <SelectItem value="07:00">7:00 AM</SelectItem>
                      <SelectItem value="08:00">8:00 AM</SelectItem>
                      <SelectItem value="09:00">9:00 AM</SelectItem>
                      <SelectItem value="10:00">10:00 AM</SelectItem>
                      <SelectItem value="12:00">12:00 PM</SelectItem>
                      <SelectItem value="17:00">5:00 PM</SelectItem>
                      <SelectItem value="18:00">6:00 PM</SelectItem>
                      <SelectItem value="19:00">7:00 PM</SelectItem>
                      <SelectItem value="20:00">8:00 PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-gray-300 mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Workout Days
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map((day) => (
                      <button
                        key={day.id}
                        onClick={() => toggleReminderDay(day.id)}
                        className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
                          notificationPrefs.reminder_days.includes(day.id)
                            ? 'bg-primary text-white'
                            : 'bg-secondary text-gray-400 hover:bg-secondary/80'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <Button
              data-testid="save-notifications-btn"
              onClick={saveNotificationPreferences}
              disabled={savingPrefs}
              className="w-full bg-primary hover:bg-red-700"
            >
              {savingPrefs ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Notification Preferences
            </Button>
          </div>
        </TabsContent>

        {/* HSA/FSA Tab */}
        <TabsContent value="hsa-fsa" className="mt-6">
          <div className="max-w-2xl space-y-6">
            {/* HSA/FSA Overview */}
            <div className="card-tactical">
              <h3 className="text-lg text-white font-semibold mb-4">HSA/FSA Payment Options</h3>
              <p className="text-gray-400 mb-6">
                Pay for eligible fitness services with your Health Savings Account (HSA) or 
                Flexible Spending Account (FSA). A Letter of Medical Necessity may be required.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="text-blue-400 font-semibold mb-1">Health Savings Account</div>
                  <div className="text-sm text-gray-400">Tax-advantaged savings for medical expenses</div>
                  <div className="text-xs text-blue-400 mt-2">2024 Limit: $4,150 individual / $8,300 family</div>
                </div>
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="text-green-400 font-semibold mb-1">Flexible Spending Account</div>
                  <div className="text-sm text-gray-400">Employer-sponsored pre-tax benefit</div>
                  <div className="text-xs text-green-400 mt-2">2024 Limit: $3,200 (use it or lose it)</div>
                </div>
              </div>
            </div>

            {/* Eligible Services */}
            <div className="card-tactical">
              <h3 className="text-lg text-white font-semibold mb-4">Eligible Services</h3>
              <div className="space-y-3">
                {[
                  { name: 'Personal Training', desc: 'One-on-one coaching (LMN required)', icon: '🏋️' },
                  { name: 'Nutrition Coaching', desc: 'Dietary guidance (LMN required)', icon: '🥗' },
                  { name: 'Weight Management', desc: 'Comprehensive programs (LMN required)', icon: '⚖️' },
                  { name: 'Fitness Assessment', desc: 'Health evaluations', icon: '📋' },
                  { name: 'Chronic Condition Programs', desc: 'Specialized exercise (LMN required)', icon: '❤️' }
                ].map((service, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg">
                    <span className="text-2xl">{service.icon}</span>
                    <div>
                      <div className="text-white font-medium">{service.name}</div>
                      <div className="text-sm text-gray-400">{service.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* LMN Status */}
            <div className="card-tactical">
              <h3 className="text-lg text-white font-semibold mb-4">Letter of Medical Necessity</h3>
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg mb-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📄</span>
                  <div>
                    <div className="text-yellow-400 font-medium">No LMN on file</div>
                    <div className="text-sm text-gray-400 mt-1">
                      To use HSA/FSA for most fitness services, you'll need a Letter of Medical 
                      Necessity from your healthcare provider prescribing exercise for a medical condition.
                    </div>
                  </div>
                </div>
              </div>
              <Button variant="outline" className="w-full border-border">
                <FileText className="w-4 h-4 mr-2" />
                Upload LMN Document
              </Button>
            </div>

            {/* Compliance Info */}
            <div className="card-tactical">
              <h3 className="text-lg text-white font-semibold mb-4">Important Information</h3>
              <div className="space-y-3 text-sm text-gray-400">
                <p>
                  <strong className="text-white">General Rule:</strong> Fitness expenses are typically 
                  NOT eligible for HSA/FSA unless prescribed by a healthcare provider for a specific 
                  medical condition.
                </p>
                <p>
                  <strong className="text-white">Qualifying Conditions:</strong> Obesity, Type 2 Diabetes, 
                  Hypertension, Chronic Back Pain, and other conditions where exercise is medically prescribed.
                </p>
                <p>
                  <strong className="text-white">Required Documentation:</strong> Keep receipts and your 
                  LMN for tax purposes. We provide HSA/FSA compliant receipts for all eligible payments.
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance" className="mt-6">
          <div className="max-w-2xl space-y-6">
            <div className="card-tactical">
              <h3 className="text-lg text-white font-semibold mb-6">Theme</h3>
              
              <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-sm">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-yellow-100'
                  }`}>
                    {theme === 'dark' ? (
                      <Moon className="w-6 h-6 text-blue-400" />
                    ) : (
                      <Sun className="w-6 h-6 text-yellow-500" />
                    )}
                  </div>
                  <div>
                    <div className="text-white font-medium">
                      {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                    </div>
                    <div className="text-sm text-gray-400">
                      {theme === 'dark' 
                        ? 'Easy on the eyes, perfect for night workouts' 
                        : 'Bright and clean, great for daytime use'}
                    </div>
                  </div>
                </div>
                <Button
                  data-testid="theme-toggle-btn"
                  onClick={toggleTheme}
                  variant="outline"
                  className="border-border hover:border-primary"
                >
                  {theme === 'dark' ? (
                    <>
                      <Sun className="w-4 h-4 mr-2" />
                      Switch to Light
                    </>
                  ) : (
                    <>
                      <Moon className="w-4 h-4 mr-2" />
                      Switch to Dark
                    </>
                  )}
                </Button>
              </div>
              
              {/* Theme Preview */}
              <div className="mt-6">
                <p className="text-sm text-gray-400 mb-3">Preview</p>
                <div className={`p-4 rounded-sm border ${
                  theme === 'dark' 
                    ? 'bg-gray-900 border-gray-700' 
                    : 'bg-white border-gray-200'
                }`}>
                  <div className={`text-lg font-semibold mb-2 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Bodies By Akeem
                  </div>
                  <div className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Your fitness journey, your way.
                  </div>
                  <div className="mt-3 flex gap-2">
                    <div className="px-3 py-1 bg-primary text-white text-sm rounded">Primary</div>
                    <div className={`px-3 py-1 text-sm rounded ${
                      theme === 'dark' 
                        ? 'bg-gray-700 text-gray-300' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>Secondary</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="mt-6">
          <div className="max-w-2xl space-y-6">
            <div className="card-tactical">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg text-white font-semibold">Export Progress Report</h3>
                  <p className="text-sm text-gray-400">Generate a PDF with your fitness data</p>
                </div>
              </div>
              
              {/* Data Preview */}
              {previewData && (
                <div className="mb-6 p-4 bg-secondary/30 rounded-sm">
                  <p className="text-sm text-gray-400 mb-2">Available Data for Export:</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Workouts:</span>
                      <span className="text-white font-mono">{previewData.available_data?.workouts || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Nutrition Logs:</span>
                      <span className="text-white font-mono">{previewData.available_data?.nutrition_logs || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Check-ins:</span>
                      <span className="text-white font-mono">{previewData.available_data?.checkins || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Period:</span>
                      <span className="text-white font-mono">{exportOptions.days} days</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Export Options */}
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">Time Period</Label>
                  <Select
                    value={exportOptions.days.toString()}
                    onValueChange={(v) => {
                      setExportOptions(prev => ({...prev, days: parseInt(v)}));
                      setTimeout(fetchExportPreview, 100);
                    }}
                  >
                    <SelectTrigger className="bg-secondary border-transparent">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="14">Last 14 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="60">Last 60 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-gray-300 mb-3 block">Include Sections</Label>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">💪</span>
                        <span className="text-white">Workout History</span>
                      </div>
                      <Switch
                        checked={exportOptions.include_workouts}
                        onCheckedChange={(checked) => setExportOptions(prev => ({...prev, include_workouts: checked}))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">🥗</span>
                        <span className="text-white">Nutrition Log</span>
                      </div>
                      <Switch
                        checked={exportOptions.include_nutrition}
                        onCheckedChange={(checked) => setExportOptions(prev => ({...prev, include_nutrition: checked}))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">📋</span>
                        <span className="text-white">Check-in Trends</span>
                      </div>
                      <Switch
                        checked={exportOptions.include_checkins}
                        onCheckedChange={(checked) => setExportOptions(prev => ({...prev, include_checkins: checked}))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">🏆</span>
                        <span className="text-white">Achievements & Stats</span>
                      </div>
                      <Switch
                        checked={exportOptions.include_achievements}
                        onCheckedChange={(checked) => setExportOptions(prev => ({...prev, include_achievements: checked}))}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <Button
                data-testid="export-pdf-btn"
                onClick={handleExportPDF}
                disabled={exporting || (!exportOptions.include_workouts && !exportOptions.include_nutrition && !exportOptions.include_checkins && !exportOptions.include_achievements)}
                className="w-full mt-6 bg-primary hover:bg-red-700"
              >
                {exporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
